
import {defineComponent, PropType} from "vue";
import {BorderRestDtoModel} from "@/models/api/pharma-cms-content-page/BorderRestDtoModel";
import {BorderSideRestDtoModel} from "@/models/api/pharma-cms-content-page/BorderSideRestDtoModel";
import BorderSideDetail from "@/components/layouts/content-pages/wysiwyg/misc/BorderSideDetail.vue";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import MetadataGroupCard from "@/components/layouts/content-pages/wysiwyg/misc/MetadataGroupCard.vue";
import {BorderRestDto, BorderSideRestDto} from "@/api/pharma-cms-content-page/models";

type BorderSideKey = 'top' | 'bottom' | 'left' | 'right';

export default defineComponent({
  name: "BorderDetail",
  mixins: [ComponentMixin],
  components: {GenericComponentWrapper, MetadataGroupCard, BorderSideDetail, InputCheckboxBoolean},
  emits: ['update:modelValue'],
  props: {
    // Null means the group is switched off.
    modelValue: {
      type: Object as PropType<BorderRestDto | null>,
      required: false,
      default: null,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.persistedDto = this.modelValue ? BorderRestDtoModel.cloneFrom(this.modelValue) : null;

    this.componentUI.setReady();
  },
  data() {
    return {
      enabled: false as boolean,
      localDto: BorderRestDtoModel.createWithDefaults() as BorderRestDto,
      // The shared section's value, mirrored onto all four sides while they are kept identical.
      sharedSide: BorderSideRestDtoModel.createWithDefaults() as BorderSideRestDto,
      // Derived from the data on load: four identical sides read back as "one border".
      useIndividualSides: false as boolean,
      // Values entered before the group was switched off, so they can be restored.
      lastActiveDto: null as BorderRestDto | null,
      // Reset baseline: the state as it is stored in the database.
      persistedDto: null as BorderRestDto | null,
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: BorderRestDto | null) {
        this.enabled = !!newValue;
        if (newValue) {
          this.localDto = BorderRestDtoModel.cloneFrom(newValue);
          this.syncFromLocalDto();
        }
      }
    },
  },
  computed: {
    sides(): {key: BorderSideKey, label: string}[] {
      return [
        {key: 'top', label: this.$t('top')},
        {key: 'bottom', label: this.$t('bottom')},
        {key: 'left', label: this.$t('left')},
        {key: 'right', label: this.$t('right')},
      ];
    },
  },
  methods: {
    /**
     * Decides how to present what was just loaded: four identical sides are shown as a single
     * shared setting, anything else opens the individual sides with the checkbox ticked.
     */
    syncFromLocalDto(): void {
      this.useIndividualSides = !this.areSidesUniform();
      this.sharedSide = this.localDto.top
        ? BorderSideRestDtoModel.cloneFrom(this.localDto.top)
        : BorderSideRestDtoModel.createWithDefaults();
    },
    areSidesUniform(): boolean {
      const [first, ...rest] = (['top', 'bottom', 'left', 'right'] as BorderSideKey[])
        .map(key => this.serializeSide(this.localDto[key]));

      return rest.every(side => side === first);
    },
    /**
     * Sides are compared on their content, not their identity. Everything goes through cloneFrom
     * first so the key order is the same regardless of where the value came from, and a missing
     * side compares equal to an explicitly null one.
     */
    serializeSide(side?: BorderSideRestDto | null): string {
      return side ? JSON.stringify(BorderSideRestDtoModel.cloneFrom(side)) : 'null';
    },
    applySharedSideToAllSides(): void {
      (['top', 'bottom', 'left', 'right'] as BorderSideKey[]).forEach(key => {
        // A clone per side: sharing one object would make editing a single side change all four.
        this.localDto[key] = BorderSideRestDtoModel.cloneFrom(this.sharedSide);
      });
    },
    onSharedSideChanged(newValue: BorderSideRestDto | null): void {
      this.sharedSide = newValue ?? BorderSideRestDtoModel.createWithDefaults();
      this.applySharedSideToAllSides();
      this.emitModelChanged();
    },
    onUseIndividualSidesChanged(newValue: boolean): void {
      this.useIndividualSides = newValue;

      if (!newValue) {
        // Collapsing back to one border: the top side is taken as the surviving value, which is
        // what the shared section was showing all along.
        this.sharedSide = this.localDto.top
          ? BorderSideRestDtoModel.cloneFrom(this.localDto.top)
          : BorderSideRestDtoModel.createWithDefaults();
        this.applySharedSideToAllSides();
        this.emitModelChanged();
      }
    },
    onEnabledChanged(newValue: boolean): void {
      if (newValue === this.enabled) {
        return;
      }
      this.enabled = newValue;

      if (newValue) {
        this.localDto = this.lastActiveDto
          ? BorderRestDtoModel.cloneFrom(this.lastActiveDto)
          : BorderRestDtoModel.createWithDefaults();
        this.syncFromLocalDto();

        this.$emit('update:modelValue', this.localDto);
      } else {
        this.lastActiveDto = BorderRestDtoModel.cloneFrom(this.localDto);

        this.$emit('update:modelValue', null);
      }
    },
    resetToPersisted(): void {
      this.localDto = this.persistedDto
        ? BorderRestDtoModel.cloneFrom(this.persistedDto)
        : BorderRestDtoModel.createWithDefaults();
      this.syncFromLocalDto();

      this.$emit('update:modelValue', this.localDto);
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    }
  }
});
